import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, InputRef, Row } from 'antd';
import { AxiosResponse } from 'axios';
import {
    TbArrowRight,
    TbBuildingEstate,
    TbChevronRight,
    TbCircleArrowUp,
    TbCircleXFilled,
    TbMist,
    TbSearch,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { ResourceDraft } from '@/hooks/useResourceDrafts';
import { EmptySection, VirtualizedList as List, SkeletonList } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import {
    CompanySearchResult,
    CompanySearchResultGroup,
    groupCompanySearchResults,
} from '@/shared/components/company-search/utils';
import { InvestmentVisibilityType, SetStateMethod } from '@/types';

import { parseInvestmentDraftValues } from '../../investmentDraft';
import AssetListItem from './layout/AssetListItem';

interface BlankInvestmentProps {
    createMore?: boolean;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<unknown> | null;
    onCreated?: () => void;
    onCreatingChange?: (creating: boolean) => void;
    onDraftValuesChange?: (values: { value: string; visibility: InvestmentVisibilityType }) => void;
    setVisibility: SetStateMethod<InvestmentVisibilityType>;
    visibility: InvestmentVisibilityType;
}

interface AssetGroupListItemProps {
    creatingAssetKey?: string;
    group: CompanySearchResultGroup;
    setInput: SetStateMethod<string>;
    setOpen: SetStateMethod<boolean>;
    visibility: InvestmentVisibilityType;
    onCreated?: () => void;
    onBeginCreatingAsset: (assetKey: string) => boolean;
    onFinishCreatingAsset: (assetKey: string) => void;
    createMore?: boolean;
}

const AssetGroupListItem: FC<AssetGroupListItemProps> = ({
    group,
    creatingAssetKey,
    setInput,
    setOpen,
    visibility,
    onCreated,
    onBeginCreatingAsset,
    onFinishCreatingAsset,
    createMore,
}): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    const { token } = useAntDesignTheme();

    return (
        <Fragment>
            <AssetListItem
                asset={group.primary}
                creatingAssetKey={creatingAssetKey}
                setOpen={setOpen}
                setInput={setInput}
                visibility={visibility}
                onCreated={onCreated}
                onBeginCreatingAsset={onBeginCreatingAsset}
                onFinishCreatingAsset={onFinishCreatingAsset}
                createMore={createMore}
            />
            {group.alternatives.length > 0 && (
                <div style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: '0.25rem 0 0.5rem 4.75rem' }}>
                    <Button
                        disabled={Boolean(creatingAssetKey)}
                        type='text'
                        size='small'
                        style={{ color: token.colorTextTertiary }}
                        onClick={() => setExpanded((value) => !value)}
                    >
                        <TbChevronRight
                            style={{
                                transform: expanded ? 'rotate(90deg)' : undefined,
                                transition: 'transform 160ms ease',
                                verticalAlign: '-0.125rem',
                            }}
                        />
                        {expanded ? 'Hide' : 'Show'} {group.alternatives.length} other{' '}
                        {group.alternatives.length === 1 ? 'listing' : 'listings'}
                    </Button>
                </div>
            )}
            {expanded &&
                group.alternatives.map((asset) => (
                    <AssetListItem
                        key={asset.entityId ?? asset.symbol}
                        asset={asset}
                        creatingAssetKey={creatingAssetKey}
                        setOpen={setOpen}
                        setInput={setInput}
                        visibility={visibility}
                        onCreated={onCreated}
                        onBeginCreatingAsset={onBeginCreatingAsset}
                        onFinishCreatingAsset={onFinishCreatingAsset}
                        createMore={createMore}
                    />
                ))}
        </Fragment>
    );
};

const BlankInvestment: FC<BlankInvestmentProps> = ({
    createMore,
    open,
    setOpen,
    initialDraft,
    onCreated,
    onCreatingChange,
    onDraftValuesChange,
    setVisibility,
    visibility,
}) => {
    // Autofocus
    const titleInputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => titleInputRef.current?.focus(), 125);
        }
    }, [open]);

    const [value, setValue] = useState('');
    const [assets, setAssets] = useState<CompanySearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [creatingAssetKey, setCreatingAssetKey] = useState<string>();
    const creatingAssetKeyRef = useRef<string>();

    const beginCreatingAsset = useCallback((assetKey: string) => {
        if (creatingAssetKeyRef.current) {
            return false;
        }

        creatingAssetKeyRef.current = assetKey;
        setCreatingAssetKey(assetKey);
        return true;
    }, []);

    const finishCreatingAsset = useCallback((assetKey: string) => {
        if (creatingAssetKeyRef.current !== assetKey) {
            return;
        }

        creatingAssetKeyRef.current = undefined;
        setCreatingAssetKey(undefined);
    }, []);

    const { planTier } = useEntitlements();

    const currentQuery = useRef<string | null>(null);
    const navigate = useNavigate();
    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (open && initialDraft?.values) {
            const initialValues = parseInvestmentDraftValues(initialDraft.values);

            setValue(initialValues.value ?? '');
            setVisibility(initialValues.visibility ?? InvestmentVisibilityType.PRIVATE);
        }
    }, [initialDraft, open, setVisibility]);

    useEffect(() => {
        onDraftValuesChange?.({ value, visibility });
    }, [onDraftValuesChange, value, visibility]);

    useEffect(() => {
        onCreatingChange?.(Boolean(creatingAssetKey));
    }, [creatingAssetKey, onCreatingChange]);

    const handleCompanySearch = async (fragment: string) => {
        if (!fragment) {
            return;
        }

        currentQuery.current = fragment;

        try {
            const res: AxiosResponse<CompanySearchResult[]> = await authorizedAxiosInstance.post(
                '/api/search/vendors/fmp/companies',
                { fragment },
            );

            if (currentQuery.current === fragment && res?.status === 200) {
                setAssets(res.data);
                setHasSearched(true);
            }
        } catch (error) {
            captureException(error);
        } finally {
            if (currentQuery.current === fragment) {
                setLoading(false);
            }
        }
    };

    // Debounce the API call, not the UI feedback
    useDebounce(
        () => {
            if (value.trim()) {
                handleCompanySearch(value.trim());
            } else {
                setAssets([]);
                setLoading(false);
                setHasSearched(false);
            }
        },
        600,
        [value],
    );

    const showEmptyState = !loading && (assets.length === 0 || !hasSearched);
    const showResults = !loading && assets.length > 0;
    const groupedAssets = useMemo(() => groupCompanySearchResults(assets), [assets]);
    return (
        <>
            <Row gutter={[8, 0]} style={{ marginTop: '0.5rem' }}>
                <Col flex='auto'>
                    <Input
                        ref={titleInputRef}
                        disabled={Boolean(creatingAssetKey)}
                        value={value}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            setValue(inputValue);

                            if (inputValue.trim()) {
                                // Immediate feedback when typing
                                setLoading(true);
                            } else {
                                // Instantly clear when input is empty
                                setLoading(false);
                                setAssets([]);
                                setHasSearched(false);
                            }
                        }}
                        inputMode='search'
                        placeholder='Search Assets...'
                        allowClear={{
                            clearIcon: <TbCircleXFilled size='1.125rem' style={{ verticalAlign: '-0.2rem' }} />,
                        }}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }} />}
                    />
                </Col>
            </Row>
            {loading && value.trim() ? (
                <SkeletonList listLength={10} maxHeight='30rem' minHeight='30rem' />
            ) : showResults ? (
                <>
                    {planTier === 'free' && (
                        <PlatformAlert
                            showIcon
                            icon={<TbMist style={{ marginTop: '0.25rem' }} />}
                            status='info'
                            message='Search results limited by current plan.'
                            description='For international assets, advanced data enrichment, and more - upgrade your plan today!'
                            style={{ margin: '1.5rem 0' }}
                            action={
                                <Button
                                    disabled={Boolean(creatingAssetKey)}
                                    type='primary'
                                    style={{ marginTop: '1.25rem' }}
                                    onClick={() => navigate(`/settings/account/plans`)}
                                >
                                    <TbCircleArrowUp
                                        size='1rem'
                                        style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }}
                                    />
                                    Upgrade Plan
                                </Button>
                            }
                        />
                    )}
                    <div style={{ margin: '0.5rem 0' }}>
                        <small style={{ color: token.colorTextTertiary }}>{assets?.length} Results</small>
                    </div>
                    <List
                        style={{ overflow: 'scroll', height: '27.75rem', borderTop: `1px solid ${token.colorBorder}` }}
                        dataSource={groupedAssets}
                        renderItem={(group: CompanySearchResultGroup) => (
                            <AssetGroupListItem
                                key={group.id}
                                group={group}
                                creatingAssetKey={creatingAssetKey}
                                setOpen={setOpen}
                                setInput={setValue}
                                visibility={visibility}
                                onCreated={onCreated}
                                onBeginCreatingAsset={beginCreatingAsset}
                                onFinishCreatingAsset={finishCreatingAsset}
                                createMore={createMore}
                            />
                        )}
                    />
                </>
            ) : (
                showEmptyState && (
                    <EmptySection
                        height='30rem'
                        icon={<TbBuildingEstate size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                        label='Search Companies'
                        description='Search by company name or ticker symbol for the best results. You can also create investments directly from a company overview in Search.'
                        extra={
                            <Button
                                disabled={Boolean(creatingAssetKey)}
                                type='text'
                                onClick={() => {
                                    setOpen(false);
                                    navigate('/search/companies');
                                }}
                            >
                                Open Company Search
                                <TbArrowRight style={{ verticalAlign: '-0.1rem', marginLeft: '0.125rem' }} />
                            </Button>
                        }
                    />
                )
            )}
        </>
    );
};

export default BlankInvestment;
